import { useEffect, useState, useCallback, useRef } from 'react';
import { getBestAuthToken } from '../../utils/authHeaders';
import type {
  Message, 
  EmmaWebSocketMessage, 
  UseEmmaWebSocketReturn,
  TypingIndicator 
} from '../../types/emma.types';
import { useEmmaContext } from '../../contexts/EmmaContext';
import { useEmmaActionExecutor } from './useEmmaActionExecutor';

interface UseEmmaWebSocketOptions {
  url?: string;
  autoReconnect?: boolean;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  sessionId?: string;
}

/**
 * Emma WebSocket Hook - Real-time communication with Emma agent backend
 * Based on existing useWebSocket.ts patterns with Emma-specific optimizations
 * Provides <100ms response acknowledgment with typing indicators
 */
export function useEmmaWebSocket(options: UseEmmaWebSocketOptions = {}): UseEmmaWebSocketReturn {
  const {
    url = `${import.meta.env.VITE_WS_BASE_URL || 'ws://localhost:8002'}/ws/emma`,
    autoReconnect = true,
    reconnectInterval = 1000, // Fast reconnect for conversational UX
    maxReconnectAttempts = 1000, // High default limit to survive developer actions
    sessionId
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'connecting' | 'disconnected'>('disconnected');
  const [lastMessage, setLastMessage] = useState<Message | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [typingIndicator, setTypingIndicator] = useState<TypingIndicator>({
    isVisible: false,
    message: '',
    startTime: new Date()
  });
  const { pageContext } = useEmmaContext();
  const { executeActions } = useEmmaActionExecutor();

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const messageQueueRef = useRef<string[]>([]);
  const acknowledgmentTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const streamingMessageRef = useRef<{ id: string; content: string; thinkingContent: string } | null>(null);

  // Generate unique message IDs for acknowledgment tracking
  const generateMessageId = useCallback(() => {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Send message with <100ms acknowledgment tracking
  const sendMessage = useCallback((content: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      // Queue message and trigger immediate connection recovery
      messageQueueRef.current.push(content);
      setError('Restoring connection...');
      if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
        setReconnectAttempts(0);
        connect();
      }
      return;
    }

    let enrichedPageContext = { ...pageContext };
    try {
      const stored = sessionStorage.getItem('emma_active_artifact');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.artifact?.slug) {
          enrichedPageContext = {
            ...enrichedPageContext,
            slug: parsed.artifact.slug,
            active_asset: parsed.artifact
          };
        }
      }
    } catch (e) {}

    const messageId = generateMessageId();
    const message: EmmaWebSocketMessage = {
      type: 'message',
      payload: {
        content,
        timestamp: new Date().toISOString(),
        sessionId,
        current_page: window.location.pathname,
        page_context: enrichedPageContext,
      },
      timestamp: new Date().toISOString(),
      messageId
    };

    try {
      wsRef.current.send(JSON.stringify(message));
      
      // Set acknowledgment timeout for <100ms response requirement
      acknowledgmentTimeoutRef.current = setTimeout(() => {
        setError('Response timeout - Emma may be experiencing delays');
      }, 100);

      setError(null);
    } catch (err) {
      setError('Failed to send message');
      messageQueueRef.current.push(content);
    }
  }, [pageContext, sessionId, generateMessageId]);

  // Process queued messages when connection is restored
  const processMessageQueue = useCallback(() => {
    while (messageQueueRef.current.length > 0 && wsRef.current?.readyState === WebSocket.OPEN) {
      const queuedMessage = messageQueueRef.current.shift();
      if (queuedMessage) {
        sendMessage(queuedMessage);
      }
    }
  }, [sendMessage]);

  // Handle incoming WebSocket messages
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      console.log('[WS Incoming]:', event.data);
      const wsMessage: EmmaWebSocketMessage = JSON.parse(event.data);
      
      // Clear acknowledgment timeout on any response
      if (acknowledgmentTimeoutRef.current) {
        clearTimeout(acknowledgmentTimeoutRef.current);
        acknowledgmentTimeoutRef.current = null;
      }

      switch (wsMessage.type) {
        case 'thinking_chunk': {
          const chunk = (wsMessage as any).chunk || (wsMessage as any).text || '';
          const msgId = wsMessage.messageId || streamingMessageRef.current?.id || `emma_stream_${Date.now()}`;
          let currentMsg = streamingMessageRef.current;
          if (!currentMsg || currentMsg.id !== msgId) {
            currentMsg = {
              id: msgId,
              content: '',
              thinkingContent: ''
            };
            streamingMessageRef.current = currentMsg;
          }
          currentMsg.thinkingContent += chunk;
          
          const message: Message = {
            id: msgId,
            content: currentMsg.content,
            timestamp: new Date(),
            sender: 'emma',
            type: 'text',
            metadata: {
              thinkingContent: currentMsg.thinkingContent
            } as any
          };
          (message as any).isChunk = true;
          setLastMessage(message);
          setTypingIndicator({ isVisible: false, message: '', startTime: new Date() });
          break;
        }

        case 'token':
        case 'chunk':
        case 'content_chunk':
        case 'text_chunk':
        case 'message_chunk': {
          const chunk = (wsMessage as any).chunk || (wsMessage as any).text || (wsMessage as any).content || '';
          const msgId = wsMessage.messageId || streamingMessageRef.current?.id || `emma_stream_${Date.now()}`;
          let currentMsg = streamingMessageRef.current;
          if (!currentMsg || currentMsg.id !== msgId) {
            currentMsg = {
              id: msgId,
              content: '',
              thinkingContent: ''
            };
            streamingMessageRef.current = currentMsg;
          }
          currentMsg.content += chunk;
          
          const message: Message = {
            id: msgId,
            content: currentMsg.content,
            timestamp: new Date(),
            sender: 'emma',
            type: 'text',
            metadata: {
              thinkingContent: currentMsg.thinkingContent
            } as any
          };
          (message as any).isChunk = true;
          setLastMessage(message);
          setTypingIndicator({ isVisible: false, message: '', startTime: new Date() });
          break;
        }

        case 'message_complete': {
          const payload = wsMessage.payload || wsMessage;
          const msgMeta = payload.metadata || wsMessage.metadata || {};
          
          const activeStreamId = streamingMessageRef.current?.id;
          const accumulatedContent = streamingMessageRef.current?.content || '';
          streamingMessageRef.current = null;
          
          const message: Message = {
            id: wsMessage.messageId || activeStreamId || `emma_${Date.now()}`,
            content: wsMessage.content || (payload as any).content || accumulatedContent,
            timestamp: new Date(wsMessage.timestamp || Date.now()),
            sender: 'emma',
            type: 'text',
            metadata: {
              ...msgMeta,
              thinkingContent: msgMeta.thinkingContent || ''
            },
            threadId: payload.threadId,
          };
          (message as any).isComplete = true;
          setLastMessage(message);
          setTypingIndicator({ isVisible: false, message: '', startTime: new Date() });

          let targetUiAction = msgMeta?.ui_action || (msgMeta as any)?.uiAction;
          const htmlContent =
            msgMeta?.visual_template ||
            msgMeta?.genui_html ||
            msgMeta?.html ||
            msgMeta?.markup ||
            (payload as any)?.visual_template ||
            (payload as any)?.genui_html ||
            targetUiAction?.visual_template ||
            targetUiAction?.genui_html ||
            targetUiAction?.content ||
            (targetUiAction?.payload && (targetUiAction.payload.visual_template || targetUiAction.payload.genui_html || targetUiAction.payload.content)) ||
            '';

          const slug = msgMeta?.slug || targetUiAction?.slug || (payload as any)?.slug;
          const previewUrl = msgMeta?.preview_url || msgMeta?.previewUrl || targetUiAction?.preview_url || targetUiAction?.previewUrl || (slug ? `/api/templates/preview/${slug}` : undefined);

          if (!targetUiAction) {
            const rawContent = message.content || '';
            const isIntermediate = rawContent.toLowerCase().includes('starting content generation') || rawContent.toLowerCase().includes('all checkpoints approved') || msgMeta?.state === 'checkpoint_presented' || msgMeta?.state === 'phase0';
            const isDocumentContent = !isIntermediate && (htmlContent.length > 0 || (rawContent.length > 250 && (rawContent.includes('# ') || rawContent.includes('## ') || rawContent.includes('```html') || rawContent.includes('<!DOCTYPE html>') || rawContent.includes('<html'))));

            if (msgMeta?.isStatelessAsset || isDocumentContent || slug || previewUrl) {
              targetUiAction = {
                action: 'OPEN_ARTIFACT_PANEL',
                artifactId: msgMeta?.artifactId || `art_${Date.now()}`,
                title: msgMeta?.title || 'Generated Artifact',
                contentType: msgMeta?.assetType || msgMeta?.contentType || 'document',
                content: htmlContent || rawContent,
                slug,
                preview_url: previewUrl,
                version: 1
              };
            }
          }

          if (targetUiAction) {
            targetUiAction.slug = targetUiAction.slug || slug;
            targetUiAction.preview_url = targetUiAction.preview_url || previewUrl;
            message.metadata.ui_action = targetUiAction;

            const validContent = (htmlContent || targetUiAction.content || (targetUiAction.payload && targetUiAction.payload.content) || '').includes('✨ **Asset Generated')
              ? ''
              : (htmlContent || targetUiAction.content || (targetUiAction.payload && targetUiAction.payload.content) || (message.content?.includes('✨ **Asset Generated') ? '' : message.content));

            message.metadata.artifact = {
              id: targetUiAction.artifactId || `art_${Date.now()}`,
              artifactId: targetUiAction.artifactId || `art_${Date.now()}`,
              title: targetUiAction.title || 'Generated Artifact',
              contentType: targetUiAction.contentType || 'document',
              content: validContent,
              slug: targetUiAction.slug || slug,
              previewUrl: targetUiAction.preview_url || previewUrl,
              version: targetUiAction.version || 1
            };
          }

          const filteredActions = [targetUiAction, (msgMeta as any)?.actions, (payload as any).actions]
            .filter(Boolean)
            .filter(act => {
              const a = typeof act === 'string' ? act : act.action;
              return a !== 'OPEN_ARTIFACT_PANEL' && a !== 'open_artifact_panel';
            });
          if (filteredActions.length > 0) {
            void executeActionsRef.current(filteredActions);
          }
          break;
        }

        case 'artifact_version': {
          const payload = wsMessage.payload || wsMessage;
          const uiAction = payload.uiAction || payload.ui_action || (payload as any).metadata?.ui_action;

          const rawArtContent = payload.content || (wsMessage as any).content || '';
          const cleanArtContent = rawArtContent.includes('✨ **Asset Generated') ? '' : rawArtContent;
          const artSlug = payload.slug || uiAction?.slug;
          const artPreviewUrl = payload.previewUrl || payload.preview_url || uiAction?.preview_url || (artSlug ? `/api/templates/preview/${artSlug}` : undefined);

          const message: Message = {
            id: wsMessage.messageId || `artifact_${Date.now()}`,
            content: cleanArtContent,
            timestamp: new Date(wsMessage.timestamp || Date.now()),
            sender: 'emma',
            type: 'text',
            metadata: {
              isArtifact: true,
              artifactId: payload.artifactId || payload.artifact_id,
              title: payload.title,
              contentType: payload.contentType || payload.content_type,
              version: payload.version || 1,
              ui_action: uiAction,
              artifact: {
                id: payload.artifactId || payload.artifact_id || `art_${Date.now()}`,
                artifactId: payload.artifactId || payload.artifact_id || `art_${Date.now()}`,
                title: payload.title || 'Generated Artifact',
                contentType: payload.contentType || payload.content_type || 'document',
                content: cleanArtContent,
                slug: artSlug,
                previewUrl: artPreviewUrl,
                version: payload.version || 1
              }
            } as any
          };
          (message as any).isComplete = true;
          setLastMessage(message);

          // Note: OPEN_ARTIFACT_PANEL is not auto-executed here so user manually clicks button
          break;
        }

        case 'error': {
          const message: Message = {
            id: wsMessage.messageId || `emma_err_${Date.now()}`,
            content: wsMessage.content || wsMessage.payload?.content || 'An error occurred',
            timestamp: new Date(wsMessage.timestamp || Date.now()),
            sender: 'emma',
            type: 'error'
          };
          (message as any).isComplete = true;
          setLastMessage(message);
          setTypingIndicator({ isVisible: false, message: '', startTime: new Date() });
          setError(message.content);
          break;
        }

        case 'message': {
          // Handle both payload-wrapped and flat response formats
          const payload = wsMessage.payload || wsMessage;
          const msgContent = payload.content || '';
          const msgMeta = payload.metadata || wsMessage.metadata || {};
          const message: Message = {
            id: wsMessage.messageId || `emma_${Date.now()}`,
            content: msgContent,
            timestamp: new Date(wsMessage.timestamp || Date.now()),
            sender: (wsMessage as any).sender || 'emma',
            type: (payload.type || 'text') as Message['type'],
            metadata: msgMeta,
            threadId: payload.threadId,
          };
          setLastMessage(message);
          setTypingIndicator({ isVisible: false, message: '', startTime: new Date() });

          const filteredMsgActions = [msgMeta?.ui_action, (msgMeta as any)?.actions, (payload as any).actions]
            .filter(Boolean)
            .filter(act => {
              const a = typeof act === 'string' ? act : act.action;
              return a !== 'OPEN_ARTIFACT_PANEL' && a !== 'open_artifact_panel';
            });
          if (filteredMsgActions.length > 0) {
            void executeActionsRef.current(filteredMsgActions);
          }
          break;
        }

        case 'typing': {
          const typingText = wsMessage.content || wsMessage.payload?.content || wsMessage.payload?.message || (wsMessage as any).message || 'Emma is thinking...';
          setTypingIndicator({
            isVisible: true,
            message: typingText,
            startTime: new Date()
          });
          break;
        }

        case 'recommendation':
          // Handle strategic recommendations
          const recommendationMessage: Message = {
            id: wsMessage.messageId || `rec_${Date.now()}`,
            content: 'I have some strategic recommendations for you:',
            timestamp: new Date(wsMessage.timestamp || Date.now()),
            sender: 'emma',
            type: 'recommendation',
            metadata: {
              agentRecommendations: wsMessage.payload?.recommendations || [],
              strategicInsights: wsMessage.payload?.insights || []
            }
          };
          setLastMessage(recommendationMessage);
          break;

        case 'emma_response': {
          // Response from orchestrator — may include ui_action for data refresh
          const resp = wsMessage.response || wsMessage.payload || {};
          const emmaMsg: Message = {
            id: wsMessage.messageId || `emma_${Date.now()}`,
            content: resp.message || resp.conversational || '',
            timestamp: new Date(),
            sender: 'emma',
            type: 'text',
            metadata: {
              ...(resp.data ? { data: resp.data } : {}),
              ...(wsMessage.metadata?.ui_action ? { ui_action: wsMessage.metadata.ui_action } : {}),
            },
          };
          setLastMessage(emmaMsg);
          setTypingIndicator({ isVisible: false, message: '', startTime: new Date() });

          const filteredRespActions = [wsMessage.metadata?.ui_action, (resp as any)?.ui_action, (resp as any)?.actions]
            .filter(Boolean)
            .filter(act => {
              const a = typeof act === 'string' ? act : act.action;
              return a !== 'OPEN_ARTIFACT_PANEL' && a !== 'open_artifact_panel';
            });
          if (filteredRespActions.length > 0) {
            void executeActionsRef.current(filteredRespActions);
          }
          break;
        }

        case 'status': {
          const isInitFrame = wsMessage.phase === 'init' || (wsMessage.content && wsMessage.content.includes('Connected to Emma'));
          if (isInitFrame) {
            console.log('📡 Connected to Emma WebSocket stream');
            break;
          }

          const typingText = wsMessage.content || wsMessage.payload?.content || wsMessage.payload?.message || (wsMessage as any).message || 'Emma is analyzing request...';
          setTypingIndicator({
            isVisible: true,
            message: typingText,
            startTime: new Date()
          });
          break;
        }

        case 'context_sync':
          // Context preservation - handled by EmmaContext
          break;

        default:
          console.warn('Unknown Emma WebSocket message type:', wsMessage.type);
      }
    } catch (err) {
      console.error('Failed to parse Emma WebSocket message:', err);
      setError('Invalid message format received');
    }
  }, []);

  const executeActionsRef = useRef(executeActions);
  useEffect(() => {
    executeActionsRef.current = executeActions;
  }, [executeActions]);

  const handleMessageRef = useRef(handleMessage);
  useEffect(() => {
    handleMessageRef.current = handleMessage;
  }, [handleMessage]);

  const processMessageQueueRef = useRef(processMessageQueue);
  useEffect(() => {
    processMessageQueueRef.current = processMessageQueue;
  }, [processMessageQueue]);

  // Connection management
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    setConnectionStatus('connecting');
    setError(null);

    try {
      // Attach the auth token so the server can authenticate the socket and
      // derive the real org/user, instead of trusting client-supplied values.
      const token = getBestAuthToken();
      const params = new URLSearchParams();
      if (sessionId) params.set('sessionId', sessionId);
      if (token) params.set('token', token);
      const qs = params.toString();
      const wsUrl = qs ? `${url}?${qs}` : url;
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        setIsConnected(true);
        setConnectionStatus('connected');
        setReconnectAttempts(0);
        setError(null);
        
        // Process any queued messages
        processMessageQueueRef.current();
      };

      wsRef.current.onmessage = (event) => {
        handleMessageRef.current(event);
      };

      wsRef.current.onerror = (error) => {
        console.error('Emma WebSocket error:', error);
        setError('Connection error occurred');
      };

      wsRef.current.onclose = (event) => {
        setIsConnected(false);
        setConnectionStatus('disconnected');
        setTypingIndicator({ isVisible: false, message: '', startTime: new Date() });

        if (autoReconnect && reconnectAttempts < maxReconnectAttempts && !event.wasClean) {
          const backoff = Math.min(reconnectInterval * Math.pow(1.5, reconnectAttempts), 10000);
          console.warn(`[Emma WebSocket] Disconnected. Reconnecting in ${(backoff / 1000).toFixed(1)}s (attempt ${reconnectAttempts + 1}/${maxReconnectAttempts})...`);
          reconnectTimeoutRef.current = setTimeout(() => {
            setReconnectAttempts(prev => prev + 1);
            connect();
          }, backoff);
        }
      };
    } catch (err) {
      setError('Failed to establish connection');
      setConnectionStatus('disconnected');
    }
  }, [url, sessionId, autoReconnect, reconnectAttempts, maxReconnectAttempts, reconnectInterval]);

  const reconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onmessage = null;
      wsRef.current.onerror = null;
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    setReconnectAttempts(0);
    connect();
  }, [connect]);

  // Initialize connection
  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (acknowledgmentTimeoutRef.current) {
        clearTimeout(acknowledgmentTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.onopen = null;
        wsRef.current.onmessage = null;
        wsRef.current.onerror = null;
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [url, sessionId]);

  return {
    isConnected,
    connectionStatus,
    sendMessage,
    lastMessage,
    typingIndicator,
    error,
    reconnect
  };
}
